import { useEffect, useState } from "react";
import { api, errDetail } from "../../lib/api";
import { useApp } from "../../lib/store";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { toast } from "sonner";
import { UserPlus, Upload, CheckCircle2, AlertTriangle } from "lucide-react";
import { ScopePicker, EMPTY_SCOPE } from "./ScopePicker";

const CSV_TEMPLATE = "email;first_name;last_name;role;plants;segments\nnew.user@labelforce.io;Max;Muster;Sales Rep;HAM;Food";

export function InviteDialog({ open, onOpenChange, roles, onDone }) {
  const { t } = useApp();
  const [tab, setTab] = useState("single");
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "", role_id: "", job_title: "", department: "", phone: "", language: "de" });
  const [scope, setScope] = useState({ ...EMPTY_SCOPE });
  const [saving, setSaving] = useState(false);
  const [csv, setCsv] = useState("");
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({ email: "", first_name: "", last_name: "", role_id: "", job_title: "", department: "", phone: "", language: "de" });
      setScope({ ...EMPTY_SCOPE });
      setCsv(""); setPreview(null); setTab("single");
    }
  }, [open]);

  const invite = async () => {
    setSaving(true);
    try {
      await api.post("/iam/users/invite", { ...form, scope });
      toast.success(t("iam_invite_sent"));
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(errDetail(e)); }
    finally { setSaving(false); }
  };

  const runBulk = async (validateOnly) => {
    setSaving(true);
    try {
      const { data } = await api.post("/iam/users/bulk-invite", { csv, validate_only: validateOnly });
      setPreview(data);
      if (!validateOnly) { toast.success(`${data.valid} ${t("iam_invite_sent")}`); onDone?.(); }
    } catch (e) { toast.error(errDetail(e)); }
    finally { setSaving(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCsv(await f.text());
    setPreview(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5 text-[#E8871E]" />{t("iam_invite_user")}</DialogTitle>
          <DialogDescription>{t("iam_invite_help")}</DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full">
            <TabsTrigger value="single" className="flex-1" data-testid="invite-tab-single">{t("iam_invite_single")}</TabsTrigger>
            <TabsTrigger value="bulk" className="flex-1" data-testid="invite-tab-bulk">{t("iam_bulk_invite")}</TabsTrigger>
          </TabsList>

          <TabsContent value="single" className="space-y-4 pt-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2 space-y-1">
                <Label>E-Mail *</Label>
                <Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  data-testid="invite-email-input" placeholder="name@firma.de" />
              </div>
              <div className="space-y-1"><Label>{t("first_name")}</Label>
                <Input value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} data-testid="invite-first-input" /></div>
              <div className="space-y-1"><Label>{t("last_name")}</Label>
                <Input value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} data-testid="invite-last-input" /></div>
              <div className="space-y-1"><Label>{t("iam_job_title")}</Label>
                <Input value={form.job_title} onChange={(e) => setForm((f) => ({ ...f, job_title: e.target.value }))} data-testid="invite-title-input" /></div>
              <div className="space-y-1"><Label>{t("iam_department")}</Label>
                <Input value={form.department} onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))} data-testid="invite-dept-input" /></div>
              <div className="sm:col-span-2 space-y-1">
                <Label>{t("role")} *</Label>
                <Select value={form.role_id} onValueChange={(v) => setForm((f) => ({ ...f, role_id: v }))}>
                  <SelectTrigger data-testid="invite-role-select"><SelectValue placeholder="–" /></SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <ScopePicker value={scope} onChange={setScope} testid="invite-scope-picker" />
            <Button className="w-full bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" disabled={saving || !form.email || !form.role_id}
              onClick={invite} data-testid="invite-submit-btn">{saving ? "…" : t("iam_send_invite")}</Button>
          </TabsContent>

          <TabsContent value="bulk" className="space-y-3 pt-3">
            <p className="text-xs text-gray-500">{t("iam_bulk_help")}</p>
            <Textarea rows={7} value={csv} onChange={(e) => { setCsv(e.target.value); setPreview(null); }}
              placeholder={CSV_TEMPLATE} className="font-mono text-xs" data-testid="bulk-csv-textarea" />
            <div className="flex flex-wrap gap-2 items-center">
              <input type="file" accept=".csv,text/csv" onChange={onFile} className="text-xs" data-testid="bulk-csv-file" />
              <Button variant="outline" size="sm" onClick={() => setCsv(CSV_TEMPLATE)} data-testid="bulk-template-btn">{t("iam_csv_template")}</Button>
              <Button size="sm" variant="outline" disabled={!csv || saving} onClick={() => runBulk(true)}
                data-testid="bulk-validate-btn"><Upload className="h-3.5 w-3.5 mr-1" />{t("iam_validate")}</Button>
              <Button size="sm" className="bg-[#E8871E] hover:bg-[#d1751a] text-white"
                disabled={!preview || saving || !preview.valid} onClick={() => runBulk(false)}
                data-testid="bulk-execute-btn">{t("iam_import_invite")} ({preview?.valid || 0})</Button>
            </div>
            {preview && (
              <div className="rounded-lg border border-gray-200 overflow-hidden" data-testid="bulk-preview">
                <div className="flex gap-3 px-3 py-2 bg-gray-50 text-xs">
                  <span className="text-emerald-700 font-semibold flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />{preview.valid}</span>
                  <span className="text-red-600 font-semibold flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />{preview.invalid}</span>
                </div>
                <div className="max-h-52 overflow-y-auto divide-y divide-gray-50">
                  {preview.rows.map((r) => (
                    <div key={r.row} className="px-3 py-2 text-xs flex flex-wrap items-center gap-2" data-testid={`bulk-row-${r.row}`}>
                      <span className="font-mono text-gray-400">#{r.row}</span>
                      <span className="font-medium">{r.email}</span>
                      <Badge variant="outline" className="text-[10px]">{r.role}</Badge>
                      {r.plants?.length > 0 && <span className="text-[10px] text-gray-500">{r.plants.join(",")}</span>}
                      {r.segments?.length > 0 && <span className="text-[10px] text-gray-500">{r.segments.join(",")}</span>}
                      {r.errors.length > 0
                        ? <span className="text-red-600 ml-auto">{r.errors.join(" · ")}</span>
                        : <span className="text-emerald-600 ml-auto">{r.created ? "✓" : "OK"}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
